import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Search, AlertCircle, Clock, MessageSquare, ChevronRight, Send, Paperclip, Download, FileText, Image, File, X, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { publicApi } from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';

const statusLabels = {
  'RECEBIDA': 'Recebida',
  'EM_TRIAGEM': 'Em Triagem',
  'ADMITIDA': 'Admitida',
  'INADMITIDA': 'Inadmitida',
  'EM_INVESTIGACAO': 'Em Investigação',
  'AGUARDA_INFO': 'Aguarda Informação',
  'PENDENTE_APROVACAO': 'Pendente Aprovação',
  'CONCLUIDA': 'Concluída',
  'ARQUIVADA': 'Arquivada'
};

const statusColors = {
  'RECEBIDA': 'bg-slate-100 text-slate-700',
  'EM_TRIAGEM': 'bg-blue-100 text-blue-700',
  'ADMITIDA': 'bg-sky-100 text-sky-700',
  'INADMITIDA': 'bg-red-100 text-red-700',
  'EM_INVESTIGACAO': 'bg-purple-100 text-purple-700',
  'AGUARDA_INFO': 'bg-amber-100 text-amber-700',
  'PENDENTE_APROVACAO': 'bg-orange-100 text-orange-700',
  'CONCLUIDA': 'bg-emerald-100 text-emerald-700',
  'ARQUIVADA': 'bg-gray-100 text-gray-500'
};

// File helpers
const getFileIcon = (type) => {
  if (type?.startsWith('image/')) return <Image className="w-4 h-4" />;
  if (type?.includes('pdf')) return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageFile = (contentType) => contentType?.startsWith('image/');

const TrackReportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [credentials, setCredentials] = useState({ tracking_code: '', pin: '' });
  usePageTitle(report?.tenant_name || null, 'Acompanhar Denúncia');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await publicApi.trackReport(credentials);
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Denúncia não encontrada ou PIN inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await publicApi.sendReporterMessage(credentials.tracking_code, credentials.pin, newMessage);
      // Refresh report to get new message
      const response = await publicApi.trackReport(credentials);
      setReport(response.data);
      setNewMessage('');
    } catch (err) {
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Ficheiro demasiado grande (máximo 25MB)');
      return;
    }
    
    setUploadingFile(true);
    setError('');
    
    try {
      await publicApi.uploadReporterAttachment(credentials.tracking_code, credentials.pin, file);
      // Refresh report
      const response = await publicApi.trackReport(credentials);
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar ficheiro');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = (attachment) => {
    window.open(
      publicApi.downloadAttachment(attachment.id, credentials.tracking_code, credentials.pin),
      '_blank'
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-slate-900" />
              <span className="font-semibold text-slate-900">COGNIEtica</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {!report ? (
          // Track Form
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-600" />
              </div>
              <CardTitle>Acompanhar Denúncia</CardTitle>
              <CardDescription>
                Introduza o código de seguimento e o PIN para consultar o estado da sua denúncia
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="mb-6 bg-red-50 border-red-200">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleTrack} className="space-y-4">
                <div>
                  <Label htmlFor="tracking_code">Código de Seguimento</Label>
                  <Input
                    id="tracking_code"
                    value={credentials.tracking_code}
                    onChange={(e) => setCredentials(prev => ({ ...prev, tracking_code: e.target.value.toUpperCase() }))}
                    placeholder="DEN-2026-XXXXX"
                    className="font-mono text-lg tracking-wider"
                    data-testid="tracking-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pin">PIN</Label>
                  <Input
                    id="pin"
                    type="password"
                    value={credentials.pin}
                    onChange={(e) => setCredentials(prev => ({ ...prev, pin: e.target.value.toUpperCase() }))}
                    placeholder="••••••••"
                    className="font-mono text-lg tracking-wider"
                    data-testid="pin-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="track-btn">
                  {loading ? 'A procurar...' : 'Consultar Denúncia'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-slate-500">
                  Não tem um código de seguimento?{' '}
                  <Link to="/denunciar/demo" className="text-sky-600 hover:underline">
                    Submeter nova denúncia
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Report Details
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-mono">{report.tracking_code}</CardTitle>
                    <CardDescription>Submetida em {formatDate(report.created_at)}</CardDescription>
                  </div>
                  <Badge className={statusColors[report.status]}>
                    {statusLabels[report.status] || report.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-500">Categoria</Label>
                      <p className="font-medium">{report.category}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-500">Prazo Feedback</Label>
                      <p className="font-medium">{formatDate(report.feedback_deadline)}</p>
                    </div>
                  </div>

                  {report.acknowledgment_sent_at && (
                    <Alert className="bg-emerald-50 border-emerald-200">
                      <Clock className="w-4 h-4 text-emerald-600" />
                      <AlertDescription className="text-emerald-700">
                        Confirmação de receção enviada em {formatDate(report.acknowledgment_sent_at)}
                      </AlertDescription>
                    </Alert>
                  )}

                  {report.result && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="font-medium text-slate-900 mb-2">Resultado</h3>
                      <Badge className={report.result === 'PROCEDENTE' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {report.result}
                      </Badge>
                      {report.result_summary && (
                        <p className="mt-2 text-sm text-slate-600">{report.result_summary}</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Anexos / Evidências
                </CardTitle>
                <CardDescription>
                  Ficheiros anexados à denúncia
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Image Gallery Preview */}
                {report.attachments && report.attachments.filter(a => isImageFile(a.content_type)).length > 0 && (
                  <div className="mb-4">
                    <Label className="text-sm text-slate-500 mb-2 block">Imagens</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {report.attachments.filter(a => isImageFile(a.content_type)).map((attachment) => (
                        <div 
                          key={attachment.id}
                          className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer group"
                          onClick={() => setLightboxImage(attachment)}
                        >
                          <img 
                            src={publicApi.previewAttachment(attachment.id, credentials.tracking_code, credentials.pin)}
                            alt={attachment.original_filename}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                            <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {report.attachments && report.attachments.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {report.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                      >
                        <div className="flex items-center gap-3">
                          {isImageFile(attachment.content_type) ? (
                            <div 
                              className="w-10 h-10 rounded overflow-hidden cursor-pointer"
                              onClick={() => setLightboxImage(attachment)}
                            >
                              <img 
                                src={publicApi.previewAttachment(attachment.id, credentials.tracking_code, credentials.pin)}
                                alt={attachment.original_filename}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            getFileIcon(attachment.content_type)
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-900">{attachment.original_filename}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(attachment.size)} • {attachment.uploaded_by === 'reporter' ? 'Enviado por si' : 'Enviado pelo gestor'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isImageFile(attachment.content_type) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setLightboxImage(attachment)}
                            >
                              <ZoomIn className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadAttachment(attachment)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4 mb-4">
                    Nenhum anexo
                  </p>
                )}

                <Separator className="my-4" />

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.mp3,.mp4,.wav,.zip"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile || (report.attachments?.length >= 10)}
                    className="w-full"
                    data-testid="upload-attachment-btn"
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    {uploadingFile ? 'A enviar...' : 'Adicionar Anexo'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {report.attachments?.length || 0} de 10 ficheiros • Máximo 25MB por ficheiro
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Mensagens
                </CardTitle>
                <CardDescription>
                  Comunicação segura com o gestor de compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                  {report.messages && report.messages.length > 0 ? (
                    report.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender_type === 'reporter'
                            ? 'bg-sky-50 ml-8'
                            : 'bg-slate-100 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-medium text-slate-500">
                            {msg.sender_type === 'reporter' ? 'Você' : 'Gestor de Compliance'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{msg.content}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">
                      Ainda não há mensagens
                    </p>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="flex gap-2">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escreva uma mensagem..."
                    rows={2}
                    className="flex-1"
                    data-testid="message-textarea"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Legal Info */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-slate-900 mb-3">Proteções Legais (Lei 93/2021)</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400" />
                    Proteção contra represálias (art.º 21)
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400" />
                    Confidencialidade da identidade garantida (art.º 13)
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400" />
                    Inversão do ónus da prova em caso de represália
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    <strong>Contactos externos:</strong>{' '}
                    <a href="https://www.menac.gov.pt" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                      MENAC
                    </a>
                    {' | '}
                    <a href="#" className="text-sky-600 hover:underline">Ministério Público</a>
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button variant="outline" onClick={() => setReport(null)}>
                Sair
              </Button>
            </div>
          </div>
        )}

        {/* Image Lightbox */}
        {lightboxImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightboxImage(null)}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setLightboxImage(null)}
            >
              <X className="w-6 h-6" />
            </Button>
            <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img 
                src={publicApi.previewAttachment(lightboxImage.id, credentials.tracking_code, credentials.pin)}
                alt={lightboxImage.original_filename}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-center">
                <p className="text-white text-sm">{lightboxImage.original_filename}</p>
                <p className="text-white/60 text-xs mt-1">
                  {formatFileSize(lightboxImage.size)} • {lightboxImage.uploaded_by === 'reporter' ? 'Enviado por si' : 'Enviado pelo gestor'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => downloadAttachment(lightboxImage)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descarregar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackReportPage;
